import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  styled,
} from "@mui/material";
import { Edit, Delete, Add, Search } from "@mui/icons-material";
// Estilos
const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    '&.MuiTableCell-head': {
        backgroundColor: '#19438C',
        color: 'white',
        fontSize: 16,
    },
    '&.MuiTableCell-body': {
        fontSize: 14,
        borderRight: 'none',
        borderLeft: 'none',
    },
}));

const API_URL = "https://bei360.injupemp.gob.hn:8444/api/rutas/";

const GestionRutas = () => {
  const [rutas, setRutas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Buscador
  const [search, setSearch] = useState("");

  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Modal formulario
  const [openForm, setOpenForm] = useState(false);

  // Modal confirmación eliminar
  const [openConfirm, setOpenConfirm] = useState(false);
  const [rutaToDelete, setRutaToDelete] = useState(null);

  const [form, setForm] = useState({
    id: null,
    nombre: "",
    direccion: "",
    estado: "activo"
  });

  useEffect(() => {
    fetchRutas();
  }, []);

  const fetchRutas = () => {
    setLoading(true);
    fetch(API_URL)
      .then((res) => {
        if (!res.ok) throw new Error("Error al obtener las rutas");
        return res.json();
      })
      .then((data) => {
        setRutas(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const method = form.id ? "PUT" : "POST";
    const url = form.id ? `${API_URL}${form.id}/` : API_URL;

    fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Error al guardar taller");
        return res.json();
      })
      .then(() => {
        fetchRutas();
        handleCloseForm();
      })
      .catch((err) => setError(err.message));
  };

  const handleEdit = (t) => {
    setForm(t);
    setOpenForm(true);
  };

  const handleDeleteRequest = (t) => {
    setRutaToDelete(t);
    setOpenConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (!rutaToDelete) return;

    fetch(`${API_URL}${rutaToDelete.id}/`, { method: "DELETE" })
      .then((res) => {
        if (!res.ok) throw new Error("Error al eliminar conductor");
        fetchRutas();
        setOpenConfirm(false);
        setRutaToDelete(null);
      })
      .catch((err) => setError(err.message));
  };

  const handleCloseForm = () => {
    setForm({
    id: null,
    nombre: "",
    direccion: "",
    estado: "activo"
  });
    setOpenForm(false);
  };

  // Paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtrado
  const filteredRutas = rutas.filter(
    (t) =>
      t.nombre.toLowerCase().includes(search.toLowerCase())
  );

  // Paginación aplicada al filtrado
  const paginatedrutas = filteredRutas.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) return <Typography>Cargando rutas...</Typography>;
  if (error) return <Typography color="error">Error: {error}</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" sx={{mb: 2}}>
        Gestión de Rutas
      </Typography>

      {/* Barra de búsqueda y botón */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <TextField
          placeholder="Buscar por nombre"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ color: "gray", mr: 1 }} />,
          }}
          sx={{ width: "40%" }}
        />

        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => setOpenForm(true)}
        >
          Nueva Ruta
        </Button>
      </Box>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <StyledTableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <StyledTableCell>ID</StyledTableCell>
              <StyledTableCell>Nombre</StyledTableCell>
              <StyledTableCell>Dirección</StyledTableCell>
              <StyledTableCell>Estado</StyledTableCell>
              <StyledTableCell align="center">Acciones</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {paginatedrutas.map((r) => (
              <StyledTableRow key={r.id}>
                <StyledTableCell>{r.id}</StyledTableCell>
                <StyledTableCell>{r.nombre}</StyledTableCell>
                <StyledTableCell>{r.direccion}</StyledTableCell>
                <StyledTableCell>{r.estado}</StyledTableCell>
                <StyledTableCell align="center">
                  <IconButton color="primary" onClick={() => handleEdit(r)}>
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteRequest(r)}
                  >
                    <Delete />
                  </IconButton>
                </StyledTableCell>
              </StyledTableRow>
            ))}
            {filteredRutas.length === 0 && (
              <StyledTableRow>
                <StyledTableCell colSpan={7} align="center">
                  No se encontraron talleres
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
          component="div"
          count={filteredRutas.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Filas por página:"
        />

      {/* Modal Formulario */}
      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {form.id ? "Editar Ruta" : "Nueva ruta"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} >
                <TextField
                  fullWidth
                  label="Nombre"
                  name="nombre"
                  value={form.nombre}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} >
                <TextField
                  fullWidth
                  label="Dirección"
                  name="direccion"
                  value={form.direccion}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Estado"
                  name="estado"
                  value={form.estado}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="activo">Activo</MenuItem>
                  <MenuItem value="inactivo">Inactivo</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} color="error">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {form.id ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmación */}
      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Seguro que deseas eliminar la ruta{" "}
            <strong>
              {rutaToDelete?.nombre} 
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)} color="error">
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionRutas;
